import api from './api';
import { mediaService } from './cmsService';
import { compressImage } from '../utils/imageCompressor';

const STATIC_CATEGORIES = ['Design', 'Engineering', 'Infrastructure', 'Sustainability', 'Technology', 'Projects', 'News'];

const API_URL = process.env.REACT_APP_API_URL || 'https://backend.designinfra.co.in/api';

// Covers are served from {host}/storage/{path}, not from under the API prefix.
const STORAGE_BASE = `${API_URL.replace(/\/api\/?$/, '')}/storage`;

/** Stored cover path (`blogs/a1b2.jpg`) → URL. Full URLs pass through untouched. */
export const blogCoverUrl = (path) => {
  if (!path) return null;
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  return `${STORAGE_BASE}/${String(path).replace(/^\/+/, '')}`;
};

/**
 * The editor embeds pasted/inserted images as base64 `data:` URLs, which can
 * run to megabytes and break the save. Upload each one to the media library
 * (folder "blogs") and point the <img> at the stored file instead.
 */
const uploadInlineImages = async (html) => {
  if (!html || !html.includes('src="data:')) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const images = Array.from(doc.querySelectorAll('img[src^="data:"]'));
  const uploaded = new Map();

  for (const img of images) {
    const src = img.getAttribute('src');
    if (!uploaded.has(src)) {
      const blob = await compressImage(await (await fetch(src)).blob());
      const ext = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg').replace('svg+xml', 'svg');
      const file = new File([blob], `blog-image-${Date.now()}.${ext}`, { type: blob.type });
      const { data } = await mediaService.upload(file, { folder: 'blogs' });
      uploaded.set(src, data.url);
    }
    img.setAttribute('src', uploaded.get(src));
  }

  return doc.body.innerHTML;
};

/**
 * Form (camelCase) → multipart payload. `cover_image` is only sent for a new
 * File, so saving without touching the cover keeps the existing one; clearing
 * it sends `remove_cover_image`.
 */
const toFormData = async (data) => {
  const fd = new FormData();
  fd.append('title', data.title ?? '');
  fd.append('excerpt', data.excerpt ?? '');
  fd.append('content', await uploadInlineImages(data.content ?? ''));
  fd.append('publish_date', data.publishDate ?? '');
  fd.append('is_draft', data.status === 'published' ? '0' : '1');
  fd.append('category', data.category ?? '');
  fd.append('meta_title', data.metaTitle ?? '');
  fd.append('meta_description', data.metaDescription ?? '');
  if (data.coverImage instanceof File) {
    fd.append('cover_image', data.coverImage);
  } else if (!data.coverImage) {
    fd.append('remove_cover_image', '1');
  }
  return fd;
};

const multipart = { headers: { 'Content-Type': 'multipart/form-data' } };

// Map API response → form (camelCase)
const fromApiFormat = (data) => ({
  id: data.id,
  slug: data.slug,
  title: data.title || '',
  excerpt: data.excerpt || '',
  content: data.content || '',
  coverImage: blogCoverUrl(data.cover_image || data.coverImage),
  category: data.category || '',
  metaTitle: data.meta_title || data.metaTitle || '',
  metaDescription: data.meta_description || data.metaDescription || '',
  publishDate: data.publish_date || data.publishDate || '',
  status: (data.is_draft === false || data.is_draft === 0) ? 'published' : 'draft',
  author: data.author || '',
  createdAt: data.created_at || data.createdAt || '',
  updatedAt: data.updated_at || data.updatedAt || ''
});

export const blogService = {
  getAll: async (params = {}) => {
    const response = await api.get('/blogs', { params });
    // Response shape: { success: true, data: { current_page, data: [...], total, last_page, per_page } }
    const paginator = response.data?.data ?? response.data;
    const list = Array.isArray(paginator.data)
      ? paginator.data
      : Array.isArray(paginator)
      ? paginator
      : [];

    return {
      data: {
        blogs: list.map(fromApiFormat),
        total: paginator.total ?? list.length,
        page: paginator.current_page ?? params.page ?? 1,
        totalPages: paginator.last_page ?? Math.ceil((paginator.total ?? list.length) / (paginator.per_page || params.limit || 10))
      }
    };
  },

  getBySlug: async (slug) => {
    const response = await api.get(`/blogs/${slug}`);
    // { success: true, data: { ...blog } }
    const blog = response.data?.data ?? response.data;
    return { data: fromApiFormat(blog) };
  },

  create: async (data) => {
    const response = await api.post('/blogs', await toFormData(data), multipart);
    const blog = response.data?.data ?? response.data;
    return { data: fromApiFormat(blog) };
  },

  // POST, not PUT — PHP does not parse multipart on PUT, so the file would be lost.
  update: async (slug, data) => {
    const response = await api.post(`/blogs/update/${slug}`, await toFormData(data), multipart);
    const blog = response.data?.data ?? response.data;
    return { data: fromApiFormat(blog) };
  },

  delete: async (slug) => {
    return api.delete(`/blogs/${slug}`);
  },

  toggleStatus: async (slug) => {
    const response = await api.patch(`/blogs/${slug}/toggle-status`);
    const blog = response.data?.data ?? response.data;
    return { data: fromApiFormat(blog) };
  },

  getCategories: async () => {
    return { data: STATIC_CATEGORIES };
  }
};
